import type { InDialogError } from '@/features/Licenses/lib/mutationHandlers'
import { Alert, Button, Dialog, HStack, VStack } from '@chakra-ui/react'
import type { ReactNode } from 'react'

type Props = {
  title: string
  errorTitle: string
  /** When undefined, the correlationId line is hidden even if present. */
  correlationIdLabel?: string
  inDialogError: InDialogError | null
  cancelLabel: string
  /**
   * Submit button rendered by the caller. Forms differ in how they expose
   * `canSubmit` / `isSubmitting` (TanStack Form's `form.Subscribe` vs. local
   * state in RevokeForm), so the button stays inline.
   */
  submitButton: ReactNode
  /**
   * Blocks the two dismissal controls this component owns — the header close
   * trigger and the footer cancel button. A form sets it while a destructive
   * request is in flight so its outcome cannot be dismissed before it is read;
   * the surrounding `Dialog.Root` has to refuse escape and backdrop itself.
   */
  dismissDisabled?: boolean
  children: ReactNode
}

/**
 * Shared chrome for the four license-action dialog forms (renew, revoke,
 * edit metadata, edit entitlements). Provides the Header (title + close
 * trigger), Body wrapper (vertical stack with the inline error alert), and
 * Footer (cancel button + caller-supplied submit button).
 */
export function LicenseFormDialog({
  title,
  errorTitle,
  correlationIdLabel,
  inDialogError,
  cancelLabel,
  submitButton,
  dismissDisabled = false,
  children,
}: Readonly<Props>) {
  return (
    <>
      <Dialog.Header pb={3}>
        <Dialog.Title>{title}</Dialog.Title>
        <Dialog.CloseTrigger disabled={dismissDisabled} />
      </Dialog.Header>
      <Dialog.Body>
        <VStack align="stretch" gap={4}>
          {inDialogError ? (
            /* `role` is explicit: Chakra v3's `Alert.Root` renders a plain
               div, and this alert appears only after a failed submit, so
               without it the failure is announced to nobody. */
            <Alert.Root status="error" role="alert">
              <Alert.Indicator />
              <Alert.Content>
                <Alert.Title>{errorTitle}</Alert.Title>
                <Alert.Description>{inDialogError.detail}</Alert.Description>
                {inDialogError.correlationId && correlationIdLabel ? (
                  <Alert.Description fontSize="xs" color="text.text-subtle">
                    {`${correlationIdLabel} ${inDialogError.correlationId}`}
                  </Alert.Description>
                ) : null}
              </Alert.Content>
            </Alert.Root>
          ) : null}
          {children}
        </VStack>
      </Dialog.Body>
      <Dialog.Footer>
        <HStack gap={2}>
          <Dialog.ActionTrigger asChild>
            <Button variant="outline" disabled={dismissDisabled}>
              {cancelLabel}
            </Button>
          </Dialog.ActionTrigger>
          {submitButton}
        </HStack>
      </Dialog.Footer>
    </>
  )
}
